import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { broadcastSettings } from 'src/composables/app/system/useBroadcastChannel'
import { accountService } from 'src/services/api/account.service'
import { log } from 'src/services/app/log.service'
import { notifyMsgs, notifyService } from 'src/services/app/notify.service'
import { themeService } from 'src/services/app/theme.service'
import { MUTATION_KEYS, QUERY_KEYS } from 'src/services/consts/queries.const'
import { wrapMutationAsync } from 'src/services/app/util.service'

/**
 * Persists settings (`theme`, `showOnboardingTour`) server-side. Every key is written to
 * the account settings optimistically in onMutate. A theme is also applied to the DOM and
 * localStorage there, so the toggle feels instant. On failure the chosen theme stays applied
 * locally; only the backend save is lost, and only a theme save tells the user.
 * The cross-tab broadcast fires in onMutate for that reason — the theme is in the DOM and in
 * shared localStorage from that moment on, so gating it on the save would leave other tabs
 * disagreeing with storage they both read. Receiving tabs apply it in useBroadcastChannel.
 * onSuccess merges only theme and showOnboardingTour. The save response omits the account-load
 * flags (allow list, block list, contact support), and spreading it would turn product policies off.
 */
export function useUpdateSettingsMutation() {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationKey: [MUTATION_KEYS.UPDATE_SETTINGS],
    mutationFn: (settings) => accountService.updateSettings(settings),

    onMutate: (settings) => {
      queryClient.setQueryData([QUERY_KEYS.ACCOUNT_DATA], (oldData) => {
        if (!oldData) return oldData
        return { ...oldData, settings: { ...oldData.settings, ...settings } }
      })
      if (!settings.theme) return
      themeService.applyTheme(settings.theme)
      broadcastSettings({ theme: settings.theme })
    },

    onSuccess: (savedSettings = {}) => {
      queryClient.setQueryData([QUERY_KEYS.ACCOUNT_DATA], (oldData) => {
        if (!oldData) return oldData
        const saved = {}
        if (savedSettings.theme) saved.theme = savedSettings.theme
        if ('showOnboardingTour' in savedSettings) {
          saved.showOnboardingTour = savedSettings.showOnboardingTour
        }
        return { ...oldData, settings: { ...oldData.settings, ...saved } }
      })
    },

    onError: (error, settings) => {
      log.error('Failed to update settings', error)
      if (settings.theme) notifyService.error(notifyMsgs.error.updateTheme)
    },
  })

  return {
    updateSettings: wrapMutationAsync(mutation.mutateAsync),
    isUpdatingSettings: mutation.isPending,
  }
}
